import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  UploadCloud, FileSpreadsheet, FileArchive, CheckCircle2, AlertTriangle, Undo2,
  Download, RefreshCcw, ArrowLeft, History, Loader2,
} from "lucide-react";

const STATUS_STYLE = {
  "NEW": "bg-emerald-100 text-emerald-800 border-emerald-200",
  "READY": "bg-emerald-100 text-emerald-800 border-emerald-200",
  "UPDATE EXISTING": "bg-blue-100 text-blue-800 border-blue-200",
  "UNCHANGED": "bg-slate-100 text-slate-600 border-slate-200",
  "CONTENT DUPLICATE": "bg-amber-100 text-amber-800 border-amber-200",
  "KEY CONFLICT": "bg-orange-100 text-orange-800 border-orange-200",
  "HASH MISMATCH": "bg-red-100 text-red-800 border-red-200",
  "NEEDS REVIEW": "bg-violet-100 text-violet-800 border-violet-200",
};
const FILTERS = ["All", "Ready", "Updates", "Duplicates", "Conflicts", "Errors", "Needs Review"];
const ERR_STATUSES = ["ERROR", "MISSING FILE", "UNSUPPORTED FILE", "INVALID SCOPE", "INVALID ACCOUNT", "INVALID GROUP", "INVALID KEY", "UNREFERENCED FILE"];

function statusBadge(s) {
  const cls = STATUS_STYLE[s] || "bg-red-100 text-red-800 border-red-200";
  return <Badge variant="outline" className={cls} data-testid={`row-status-${s.replace(/\s+/g, "-").toLowerCase()}`}>{s}</Badge>;
}

export default function KnowledgeImport() {
  const navigate = useNavigate();
  const { ctx } = useAuth();
  const [tab, setTab] = useState("import");
  const [zipFile, setZipFile] = useState(null);
  const [manifest, setManifest] = useState(null);
  const [validating, setValidating] = useState(false);
  const [batch, setBatch] = useState(null);
  const [rows, setRows] = useState([]);
  const [unreferenced, setUnreferenced] = useState([]);
  const [selected, setSelected] = useState({});
  const [overrides, setOverrides] = useState({});
  const [filter, setFilter] = useState("All");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState(null);
  const [batches, setBatches] = useState([]);
  const [rollbackTarget, setRollbackTarget] = useState(null);
  const [publishTarget, setPublishTarget] = useState(null);
  const zipRef = useRef(), manRef = useRef();

  const loadBatches = async () => {
    try { const { data } = await api.get("/knowledge/import/batches"); setBatches(data.batches || []); }
    catch (e) { /* ignore */ }
  };
  useEffect(() => { loadBatches(); }, []);

  const doValidate = async () => {
    if (!zipFile || !manifest) { toast.error("Select both a ZIP and a CSV manifest."); return; }
    setValidating(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("zip_file", zipFile);
      fd.append("manifest", manifest);
      const { data } = await api.post("/knowledge/import/validate", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBatch(data.batch); setRows(data.rows || []); setUnreferenced(data.unreferenced || []);
      const sel = {};
      (data.rows || []).forEach((r) => { if (r.selectable) sel[r.id] = true; });
      setSelected(sel); setOverrides({}); setFilter("All");
      toast.success(`Validated ${data.batch.manifest_rows} manifest rows — no production data changed.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Validation failed.");
    } finally { setValidating(false); }
  };

  const filtered = rows.filter((r) => {
    if (filter === "All") return true;
    if (filter === "Ready") return r.status === "NEW" || r.status === "READY";
    if (filter === "Updates") return r.status === "UPDATE EXISTING";
    if (filter === "Duplicates") return r.status === "CONTENT DUPLICATE" || r.status === "UNCHANGED";
    if (filter === "Conflicts") return r.status === "KEY CONFLICT" || r.status === "HASH MISMATCH";
    if (filter === "Errors") return ERR_STATUSES.includes(r.status);
    if (filter === "Needs Review") return r.status === "NEEDS REVIEW";
    return true;
  });

  const selectedCount = rows.filter((r) => selected[r.id] && r.selectable).length;

  const doExecute = async () => {
    setExecuting(true);
    try {
      const row_ids = rows.filter((r) => selected[r.id] && r.selectable).map((r) => r.id);
      const { data } = await api.post(`/knowledge/import/${batch.id}/execute`, { row_ids, overrides });
      setResult(data); setConfirmOpen(false);
      toast.success(`Import complete — ${data.created} created, ${data.updated} updated.`);
      loadBatches();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed.");
    } finally { setExecuting(false); }
  };

  const doReconcile = async (bid) => {
    try {
      const { data } = await api.post(`/knowledge/import/${bid}/reconcile`);
      toast.success(`Reconcile: ${data.total} sources, ${data.pending_certification} pending certification.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Reconcile failed."); }
  };

  const doRollback = async () => {
    try {
      const { data } = await api.post(`/knowledge/import/${rollbackTarget.id}/rollback`, { confirm: "ROLLBACK" });
      if (data.protected?.length) toast.warning(`${data.removed} removed, ${data.reverted} reverted, ${data.protected.length} protected (modified after import).`);
      else toast.success(`Rolled back — ${data.removed} removed, ${data.reverted} reverted.`);
      setRollbackTarget(null); loadBatches();
      if (result && batch && batch.id === rollbackTarget.id) setResult({ ...result, rolled_back: true });
    } catch (e) { toast.error(e?.response?.data?.detail || "Rollback failed."); }
  };

  const doPublish = async (bid, mode) => {
    try {
      const { data } = await api.post(`/knowledge/import/${bid}/publish-reviewed`, { mode });
      const sk = data.skipped_needs_review ? `, ${data.skipped_needs_review} still need review` : "";
      const cf = data.conflicts?.length ? `, ${data.conflicts.length} version conflict(s)` : "";
      toast.success(`Certified ${data.published} source(s)${sk}${cf}.`);
      setPublishTarget(null); loadBatches();
    } catch (e) { toast.error(e?.response?.data?.detail || "Publish failed."); }
  };

  const setOv = (id, key, val) => setOverrides((p) => ({ ...p, [id]: { ...(p[id] || {}), [key]: val } }));

  // ---------- RESULT SCREEN ----------
  if (result) {
    const c = result.counts || {};
    return (
      <div className="max-w-4xl mx-auto p-6 space-y-6" data-testid="kb-import-result">
        <div className="flex items-center gap-3">
          <CheckCircle2 className="h-8 w-8 text-emerald-600" />
          <div>
            <h1 className="text-2xl font-semibold">Knowledge Base Import Complete</h1>
            <p className="text-sm text-muted-foreground">Batch {result.batch_code}</p>
          </div>
        </div>
        <Card>
          <CardContent className="grid grid-cols-2 sm:grid-cols-4 gap-4 p-6">
            {[["Imported", result.created], ["Updated", result.updated], ["Skipped", result.skipped],
              ["Unchanged", c.unchanged], ["Duplicates", c.duplicates], ["Conflicts", c.conflicts],
              ["Errors", c.errors]].map(([l, v]) => (
              <div key={l} className="text-center">
                <div className="text-3xl font-bold">{v ?? 0}</div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground">{l}</div>
              </div>
            ))}
          </CardContent>
        </Card>
        <div className="flex flex-wrap gap-3">
          <Button variant="outline" onClick={() => navigate("/admin/knowledge")} data-testid="result-view-sources-btn">
            View Imported Sources
          </Button>
          <Button variant="outline" onClick={() => window.open(`${api.defaults.baseURL}/knowledge/import/batches/${batch.id}/results.csv`, "_blank")} data-testid="result-download-csv-btn">
            <Download className="h-4 w-4 mr-1" /> Download Results CSV
          </Button>
          <Button variant="outline" onClick={() => doReconcile(batch.id)} data-testid="result-reconcile-btn">
            <RefreshCcw className="h-4 w-4 mr-1" /> Run Reconciliation
          </Button>
          <Button variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" onClick={() => setPublishTarget(batch)} data-testid="result-publish-btn">
            <CheckCircle2 className="h-4 w-4 mr-1" /> Certify (Publish)
          </Button>
          <Button variant="destructive" onClick={() => setRollbackTarget(batch)} data-testid="result-rollback-btn">
            <Undo2 className="h-4 w-4 mr-1" /> Rollback Batch
          </Button>
          <Button onClick={() => { setResult(null); setBatch(null); setRows([]); setZipFile(null); setManifest(null); }} data-testid="result-new-import-btn">
            New Import
          </Button>
        </div>
        <RollbackDialog target={rollbackTarget} onClose={() => setRollbackTarget(null)} onConfirm={doRollback} />
        <PublishDialog target={publishTarget} onClose={() => setPublishTarget(null)} onConfirm={doPublish} />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <UploadCloud className="h-6 w-6 text-primary" /> Bulk Knowledge Base Import
          </h1>
          <p className="text-sm text-muted-foreground">Upload a ZIP of staged documents + a CSV manifest. Validate first — nothing is written until you confirm.</p>
        </div>
        <Button variant="ghost" onClick={() => navigate("/admin/knowledge")} data-testid="kb-import-back-btn">
          <ArrowLeft className="h-4 w-4 mr-1" /> Knowledge Center
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="import" data-testid="tab-import">Import</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history" onClick={loadBatches}>
            <History className="h-4 w-4 mr-1" /> Import History
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {tab === "history" ? (
        <Card>
          <CardHeader><CardTitle>Import History</CardTitle></CardHeader>
          <CardContent>
            <Table>
              <TableHeader><TableRow>
                <TableHead>Batch</TableHead><TableHead>Status</TableHead><TableHead>Rows</TableHead>
                <TableHead>Imported</TableHead><TableHead>Updated</TableHead><TableHead>Created By</TableHead>
                <TableHead>Date</TableHead><TableHead></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {batches.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-6">No imports yet.</TableCell></TableRow>}
                {batches.map((b) => (
                  <TableRow key={b.id} data-testid={`batch-row-${b.code}`}>
                    <TableCell className="font-mono text-xs">{b.code}</TableCell>
                    <TableCell>{b.rolled_back ? <Badge variant="outline" className="bg-slate-100">Rolled Back</Badge> : <Badge variant="outline" className={b.status === "completed" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}>{b.status}</Badge>}</TableCell>
                    <TableCell>{b.manifest_rows}</TableCell>
                    <TableCell>{b.counts?.imported ?? 0}</TableCell>
                    <TableCell>{b.counts?.updated ?? 0}</TableCell>
                    <TableCell className="text-sm">{b.created_by_name}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{(b.created_at || "").slice(0, 10)}</TableCell>
                    <TableCell className="text-right space-x-1">
                      {b.status === "completed" && !b.rolled_back && (
                        <>
                          <Button size="sm" variant="ghost" onClick={() => doReconcile(b.id)} title="Reconcile"><RefreshCcw className="h-4 w-4" /></Button>
                          <Button size="sm" variant="ghost" className="text-emerald-700" onClick={() => setPublishTarget(b)} title="Certify (Publish)"><CheckCircle2 className="h-4 w-4" /></Button>
                          <Button size="sm" variant="ghost" onClick={() => setRollbackTarget(b)} title="Rollback"><Undo2 className="h-4 w-4" /></Button>
                          <Button size="sm" variant="ghost" onClick={() => window.open(`${api.defaults.baseURL}/knowledge/import/batches/${b.id}/results.csv`, "_blank")} title="Results CSV"><Download className="h-4 w-4" /></Button>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : (
        <>
          {!rows.length && (
            <Card>
              <CardContent className="p-6 grid sm:grid-cols-2 gap-6">
                <label className="border-2 border-dashed rounded-lg p-6 flex flex-col items-center gap-2 cursor-pointer hover:bg-muted/50" data-testid="kb-import-zip-drop">
                  <FileArchive className="h-8 w-8 text-muted-foreground" />
                  <span className="font-medium">{zipFile ? zipFile.name : "Select Documents ZIP"}</span>
                  <span className="text-xs text-muted-foreground">PDF, DOCX, TXT, CSV, HTML (nested folders OK)</span>
                  <input ref={zipRef} type="file" accept=".zip" className="hidden" data-testid="kb-import-zip-input"
                    onChange={(e) => setZipFile(e.target.files?.[0] || null)} />
                </label>
                <label className="border-2 border-dashed rounded-lg p-6 flex flex-col items-center gap-2 cursor-pointer hover:bg-muted/50" data-testid="kb-import-manifest-drop">
                  <FileSpreadsheet className="h-8 w-8 text-muted-foreground" />
                  <span className="font-medium">{manifest ? manifest.name : "Select CSV Manifest"}</span>
                  <span className="text-xs text-muted-foreground">SuggestedScope, KBSection, ProposedContentKey, StagedFullName, SHA256…</span>
                  <input ref={manRef} type="file" accept=".csv" className="hidden" data-testid="kb-import-manifest-input"
                    onChange={(e) => setManifest(e.target.files?.[0] || null)} />
                </label>
                <div className="sm:col-span-2 flex justify-end">
                  <Button onClick={doValidate} disabled={validating || !zipFile || !manifest} data-testid="kb-import-validate-btn">
                    {validating ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Validating…</> : "Validate / Preview Import"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {rows.length > 0 && batch && (
            <>
              <Card>
                <CardContent className="p-4 flex flex-wrap items-center gap-4">
                  <div className="font-mono text-xs text-muted-foreground">{batch.code}</div>
                  {Object.entries(batch.summary?.by_status || {}).map(([k, v]) => (
                    <span key={k} className="text-xs">{statusBadge(k)} <b>{v}</b></span>
                  ))}
                  {!!unreferenced.length && <span className="text-xs text-amber-700">{unreferenced.length} unreferenced file(s) in ZIP</span>}
                </CardContent>
              </Card>

              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex gap-1 flex-wrap">
                  {FILTERS.map((f) => (
                    <Button key={f} size="sm" variant={filter === f ? "default" : "outline"} onClick={() => setFilter(f)} data-testid={`filter-${f.replace(/\s+/g, "-").toLowerCase()}`}>{f}</Button>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => { setRows([]); setBatch(null); }} data-testid="kb-import-cancel-btn">Cancel</Button>
                  <Button disabled={selectedCount === 0} onClick={() => setConfirmOpen(true)} data-testid="kb-import-execute-open-btn">
                    Import {selectedCount} Record{selectedCount === 1 ? "" : "s"}
                  </Button>
                </div>
              </div>

              <Card>
                <CardContent className="p-0 overflow-x-auto">
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead className="w-8"></TableHead>
                      <TableHead>Status</TableHead><TableHead>File</TableHead><TableHead>Display Title</TableHead>
                      <TableHead>Scope</TableHead><TableHead>Category</TableHead><TableHead>Content Key</TableHead>
                      <TableHead>Effective</TableHead><TableHead>Expires</TableHead>
                      <TableHead>Existing / Notes</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {filtered.map((r) => (
                        <TableRow key={r.id} data-testid={`import-row-${r.idx}`}>
                          <TableCell>
                            <input type="checkbox" disabled={!r.selectable} checked={!!selected[r.id]}
                              onChange={(e) => setSelected((p) => ({ ...p, [r.id]: e.target.checked }))}
                              data-testid={`row-select-${r.idx}`} />
                          </TableCell>
                          <TableCell>{statusBadge(r.status)}</TableCell>
                          <TableCell className="text-xs max-w-[160px] truncate" title={r.staged_filename}>{r.staged_filename}</TableCell>
                          <TableCell className="min-w-[180px]">
                            {r.selectable ? (
                              <Input className="h-8 text-sm" defaultValue={r.display_title}
                                onChange={(e) => setOv(r.id, "display_title", e.target.value)}
                                data-testid={`row-title-${r.idx}`} />
                            ) : <span className="text-sm text-muted-foreground">{r.display_title}</span>}
                          </TableCell>
                          <TableCell className="text-xs">
                            {r.scope_type || r.manifest_scope}
                            {r.applicable_states?.length ? <span className="text-muted-foreground"> · {r.applicable_states.join(",")}</span> : null}
                          </TableCell>
                          <TableCell className="text-xs">{r.category || "—"}</TableCell>
                          <TableCell className="font-mono text-[11px] max-w-[160px] truncate" title={r.content_key}>{r.content_key || "—"}</TableCell>
                          <TableCell className="min-w-[120px]">
                            {r.selectable ? (
                              <Input type="date" className="h-8 text-xs" defaultValue={r.effective_date || ""}
                                onChange={(e) => setOv(r.id, "effective_date", e.target.value)}
                                data-testid={`row-effective-${r.idx}`} />
                            ) : <span className="text-xs text-muted-foreground">{r.effective_date || "—"}</span>}
                          </TableCell>
                          <TableCell className="min-w-[120px]">
                            {r.selectable ? (
                              <Input type="date" className="h-8 text-xs" defaultValue={r.expiration_date || ""}
                                onChange={(e) => setOv(r.id, "expiration_date", e.target.value)}
                                data-testid={`row-expires-${r.idx}`} />
                            ) : <span className="text-xs text-muted-foreground">{r.expiration_date || "—"}</span>}
                          </TableCell>
                          <TableCell className="text-[11px] text-muted-foreground max-w-[220px]">
                            {r.existing_match_title && <div className="text-blue-700">↪ {r.existing_match_title} (v{r.existing_match_version})</div>}
                            {r.audience ? <div className="text-slate-500">Audience: {r.audience}</div> : null}
                            {(r.messages || []).join(" ")}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </>
          )}
        </>
      )}

      {/* Confirm execute */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="kb-import-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Confirm Import</DialogTitle>
            <DialogDescription>Imported records are created as <b>Draft</b> and flagged for review — never auto-published.</DialogDescription>
          </DialogHeader>
          <div className="text-sm space-y-1">
            <p><b>{selectedCount}</b> selected record(s) will be imported or updated.</p>
            <p className="text-muted-foreground">NEW: {rows.filter((r) => selected[r.id] && r.status === "NEW").length} · Updates: {rows.filter((r) => selected[r.id] && r.status === "UPDATE EXISTING").length}</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button onClick={doExecute} disabled={executing} data-testid="kb-import-confirm-btn">
              {executing ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Importing…</> : "Confirm & Import"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <RollbackDialog target={rollbackTarget} onClose={() => setRollbackTarget(null)} onConfirm={doRollback} />
      <PublishDialog target={publishTarget} onClose={() => setPublishTarget(null)} onConfirm={doPublish} />
    </div>
  );
}

function PublishDialog({ target, onClose, onConfirm }) {
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="kb-publish-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-emerald-600" /> Certify Import Batch</DialogTitle>
          <DialogDescription>
            Publish imported sources from <b>{target?.code}</b> so they go live for the AI Copilot and Knowledge Center.
            <b> Publish Reviewed</b> certifies only sources already marked reviewed. <b>Publish All</b> also certifies
            sources still pending review — use with care. A version conflict (another published doc with the same key) is skipped.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="outline" onClick={() => onConfirm(target.id, "all")} data-testid="kb-publish-all-btn">Publish All</Button>
          <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => onConfirm(target.id, "reviewed")} data-testid="kb-publish-reviewed-btn">Publish Reviewed</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RollbackDialog({ target, onClose, onConfirm }) {
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="kb-rollback-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-amber-600" /> Rollback Import</DialogTitle>
          <DialogDescription>
            This removes records created by <b>{target?.code}</b> and restores prior versions for updated records.
            Records modified after import are protected and skipped. Audit history is preserved.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="destructive" onClick={onConfirm} data-testid="kb-rollback-confirm-btn">Confirm Rollback</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
